import { umbUrlPatternToString } from '@umbraco-cms/backoffice/utils';

export type UmbPathPatternParamsType = { [key: string]: any };

// Replaces property union type null with 'null' [NL]
type ReplaceNull<T> = T extends null ? 'null' : T;
export type UmbPathPatternTypeAsEncodedParamsType<T> = {
	[P in keyof T]: T[P] extends (infer R)[] ? ReplaceNull<R>[] : ReplaceNull<T[P]>;
};

export class UmbPathPattern<
	LocalParamsType extends UmbPathPatternParamsType = UmbPathPatternParamsType,
	BaseParamsType extends UmbPathPatternParamsType = LocalParamsType,
> {
	#local: string;
	#base: string;

	/**
	 * Get the params type of the path pattern
	 * @public
	 * @type      {LocalParamsType}
	 * @memberof UmbPathPattern
	 * @example   `typeof MyPathPattern.PARAMS`
	 */
	readonly PARAMS!: LocalParamsType;

	/**
	 * Get absolute params type of the path pattern
	 * @public
	 * @type      {LocalParamsType & BaseParamsType}
	 * @memberof UmbPathPattern
	 * @example   `typeof MyPathPattern.ABSOLUTE_PARAMS`
	 */
	readonly ABSOLUTE_PARAMS!: LocalParamsType & BaseParamsType;

	constructor(localPattern: string, basePath?: UmbPathPattern | string) {
		this.#local = localPattern;
		// Use toAbsolutePatternString() for UmbPathPattern to preserve the full path chain
		const baseString =
			basePath instanceof UmbPathPattern ? basePath.toAbsolutePatternString() : (basePath?.toString() ?? '');
		this.#base = baseString.length > 0 && !baseString.endsWith('/') ? baseString + '/' : baseString;
	}

	generateLocal(params: LocalParamsType) {
		return umbUrlPatternToString(this.#local, params);
	}
	/**
	 * generate an absolute path from the path pattern
	 * @param {LocalParamsType} params - The local pattern parameters.
	 * @returns {string} The generated absolute path.
	 */
	generateAbsolute(params: LocalParamsType & BaseParamsType) {
		return (
			(this.#base.indexOf(':') !== -1 ? umbUrlPatternToString(this.#base, params) : this.#base) +
			umbUrlPatternToString(this.#local, params)
		);
	}

	/**
	 * Get the full absolute pattern string including base path.
	 * Use this when chaining patterns to preserve the full path.
	 * @returns {string} The complete pattern string (base + local)
	 */
	toAbsolutePatternString(): string {
		return this.#base + this.#local;
	}

	toString() {
		return this.#local;
	}
}
